if (!customElements.get('cart-priority-support')) {
  customElements.define(
    'cart-priority-support',
    class CartPrioritySupport extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="checkbox"]');
        if (!this.input || this.bound) return;
        this.bound = true;
        this.input.addEventListener('change', this.onChange.bind(this));
      }

      sectionsUrl() {
        // Keep preview_theme_id / other query params so draft theme HTML is returned.
        return `${window.location.pathname}${window.location.search}`;
      }

      getSectionsToRender() {
        const sections = [];

        const cartItems = document.getElementById('main-cart-items');
        if (cartItems?.dataset.id) {
          sections.push({
            id: 'main-cart-items',
            section: cartItems.dataset.id,
            selector: '.js-contents',
          });
        }

        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter?.dataset.id) {
          sections.push({
            id: 'main-cart-footer',
            section: cartFooter.dataset.id,
            selector: '.js-contents',
          });
        }

        const cartIcon = document.querySelector('#cart-icon-bubble-short')
          ? 'cart-icon-bubble-short'
          : 'cart-icon-bubble';
        const cartIconMobile = document.querySelector('#cart-icon-bubble--mobile-short')
          ? 'cart-icon-bubble--mobile-short'
          : 'cart-icon-bubble--mobile';

        if (document.getElementById(cartIcon)) {
          sections.push({ id: cartIcon, section: cartIcon, selector: '.shopify-section' });
        }
        if (document.getElementById(cartIconMobile)) {
          sections.push({ id: cartIconMobile, section: cartIconMobile, selector: '.shopify-section' });
        }

        return sections;
      }

      getSectionInnerHTML(html, selector) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const el = doc.querySelector(selector);
        return el ? el.innerHTML : null;
      }

      renderSections(sectionsHtml) {
        if (!sectionsHtml) return;

        this.getSectionsToRender().forEach((section) => {
          const root = document.getElementById(section.id);
          const html = sectionsHtml[section.section];
          if (!root || !html) return;

          // Never replace cart footer with HTML that lost the checkbox control
          // (e.g. live theme response while previewing a draft).
          if (section.id === 'main-cart-footer' && !html.includes('cart-priority-support')) {
            return;
          }

          const elementToReplace = root.querySelector(section.selector) || root;
          const newHtml = this.getSectionInnerHTML(html, section.selector);
          if (newHtml !== null) {
            elementToReplace.innerHTML = newHtml;
          }
        });
      }

      async fetchCart() {
        const response = await fetch(`${routes.cart_url}.js`, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Cart fetch failed');
        return response.json();
      }

      findLine(cart, variantId) {
        const id = String(variantId);
        return (cart.items || []).find((item) => String(item.id) === id || String(item.variant_id) === id);
      }

      setEmptyState(itemCount) {
        const cartItems = document.querySelector('cart-items');
        const cartFooter = document.getElementById('main-cart-footer');
        const isEmpty = !itemCount;
        if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);
        if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
      }

      syncCheckbox(shouldBeChecked) {
        const hosts = document.querySelectorAll('cart-priority-support');
        hosts.forEach((host) => {
          host.classList.remove('is-loading');
          const input = host.querySelector('input[type="checkbox"]');
          if (!input) return;
          input.disabled = false;
          input.checked = shouldBeChecked;
          if (shouldBeChecked) {
            host.dataset.lineKey = host.dataset.lineKey || '1';
          } else {
            host.dataset.lineKey = '';
          }
        });
      }

      async onChange(event) {
        const checked = event.target.checked;
        const variantId = this.dataset.variantId;
        if (!variantId) return;

        this.input.disabled = true;
        this.classList.add('is-loading');

        try {
          const cartBefore = await this.fetchCart();
          const existing = this.findLine(cartBefore, variantId);
          const sectionIds = this.getSectionsToRender().map((section) => section.section);
          const sections_url = this.sectionsUrl();

          let parsedState;

          if (checked) {
            if (existing) {
              this.syncCheckbox(true);
              return;
            }
            const addResponse = await fetch(routes.cart_add_url, {
              ...fetchConfig(),
              body: JSON.stringify({
                id: variantId,
                quantity: 1,
                sections: sectionIds.join(','),
                sections_url,
              }),
            });
            parsedState = await addResponse.json();
            if (parsedState.status || parsedState.errors || parsedState.message === 'Cart Error') {
              throw new Error(parsedState.description || parsedState.message || 'Add failed');
            }
          } else {
            if (!existing) {
              this.syncCheckbox(false);
              return;
            }
            const changeResponse = await fetch(routes.cart_change_url, {
              ...fetchConfig(),
              body: JSON.stringify({
                id: existing.key,
                quantity: 0,
                sections: sectionIds.join(','),
                sections_url,
              }),
            });
            parsedState = await changeResponse.json();
            if (parsedState.status || parsedState.errors) {
              throw new Error(parsedState.description || parsedState.message || 'Remove failed');
            }
          }

          if (parsedState.sections) {
            this.renderSections(parsedState.sections);
          }

          const cartAfter = await this.fetchCart();
          const lineAfter = this.findLine(cartAfter, variantId);
          this.setEmptyState(cartAfter.item_count);
          this.syncCheckbox(Boolean(lineAfter));

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-items',
              cartData: cartAfter,
              variantId,
            });
          }
        } catch (error) {
          console.error(error);
          const cartNow = await this.fetchCart().catch(() => null);
          const stillThere = cartNow ? Boolean(this.findLine(cartNow, variantId)) : false;
          this.syncCheckbox(stillThere);

          const errors = document.getElementById('cart-errors');
          if (errors) {
            errors.textContent = window.cartStrings?.error || 'Cart error';
          }
        } finally {
          this.classList.remove('is-loading');
          if (this.input.isConnected) this.input.disabled = false;
          const fresh = document.querySelector('cart-priority-support input[type="checkbox"]');
          if (fresh) fresh.disabled = false;
        }
      }
    }
  );
}
